import {
  MedicineBoxOutlined,
  RiseOutlined,
  SolutionOutlined,
  BarChartOutlined,
  InboxOutlined,
  WarningOutlined,
  AudioOutlined,
  OpenAIOutlined,
  SettingOutlined,
  QuestionCircleOutlined,
  SyncOutlined,
  CompassOutlined,
  ReconciliationOutlined,
  DollarCircleFilled,
  FieldTimeOutlined,
  AppstoreOutlined,
  ShoppingCartOutlined,
  SignatureOutlined,
} from "@ant-design/icons";
import styles from "./styles.module.scss";
import { routes } from "../../router/mobile/routes";
import { isDeviceOrdersAllowedHost } from "../../util/subdomain";

/** Inline Project Neo logo, sized to match the surrounding antd icons.
 *
 *  The glyph is a rounded arc over a dot — an arched "hello" silhouette
 *  that reads as a friendly face / greeting at a glance. The SVG is
 *  inlined (rather than imported as a URL and rendered via ``<img>``)
 *  so it inherits ``font-size`` from ``styles.footerIcon`` exactly like
 *  the antd icons around it, and uses ``currentColor`` for stroke/fill
 *  so active/hover states recolour it along with its neighbours.
 *
 *  The viewBox is a 64×64 square (same aspect as antd's icons) and the
 *  wrapper renders at ``1em × 1em`` so the mark lines up visually with
 *  the other line-art glyphs in the sidebar. */
const ProjectNeoIcon = ({ className }: { className?: string }) => (
  <span
    role="img"
    aria-label="neo"
    className={`anticon ${className ?? ""}`}
    style={{ display: "inline-flex", alignItems: "center" }}
  >
    <svg
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      fill="currentColor"
    >
      <path
        d="M10 42 Q32 6 54 42"
        stroke="currentColor"
        strokeWidth={6}
        strokeLinecap="round"
        fill="none"
      />
      <circle cx={32} cy={50} r={4.5} fill="currentColor" />
    </svg>
  </span>
);

/** Sidebar entry for Project Neo. Exported so the layout can place it
 *  conditionally for users with Neo access without re-describing the icon. */
export const neoPremiumTab: ITabObject = {
  key: routes.neo.path,
  title: "Neo",
  icon: <ProjectNeoIcon className={styles.footerIcon} />,
};

const deviceOrdersTabs: ITabObject[] = isDeviceOrdersAllowedHost()
  ? [
      {
        key: routes.deviceOrders.path,
        title: "Device Orders",
        icon: <ShoppingCartOutlined className={styles.footerIcon} />,
      },
    ]
  : [];

export const tabs: ITabObject[] = [
  {
    key: routes.overview.path,
    title: "Dashboard",
    icon: <AppstoreOutlined className={styles.footerIcon} />,
  },
  ...deviceOrdersTabs,
  {
    key: routes.dashboard.path,
    title: "Inbox",
    icon: <InboxOutlined className={styles.footerIcon} />,
    topBarExtraIcons: "FilterIcon",
  },
  {
    key: routes.Insights.path,
    title: "Insights",
    icon: <BarChartOutlined className={styles.footerIcon} />,
  },
  {
    key: routes.clinical.path,
    title: "Clinical",
    icon: <MedicineBoxOutlined className={styles.footerIcon} />,
  },
  {
    key: routes.watchlist.path,
    title: "Watchlist",
    icon: <RiseOutlined className={styles.footerIcon} />,
  },
  // {
  //   key: routes.vitalSync.path,
  //   title: "Vitals",
  //   icon: <SyncOutlined className={styles.footerIcon} />,
  // },
  {
    key: routes.motionAndExits.path,
    title: "Bed Exits",
    icon: <WarningOutlined className={styles.footerIcon} />,
  },
  {
    key: routes.myFacility.path,
    title: "Consents & devices",
    icon: <SolutionOutlined className={styles.footerIcon} />,
  },
];

export const tabsNP: ITabObject[] = [
  {
    key: routes.evaluationAndManagement.path, // for NPs make the default tab "Navigator"
    title: "Navigator",
    icon: <CompassOutlined className={styles.footerIcon} />,
  },
  ...deviceOrdersTabs,
  {
    key: routes.overview.path,
    title: "Dashboard",
    icon: <AppstoreOutlined className={styles.footerIcon} />,
  },
  {
    key: routes.dashboard.path,
    title: "Inbox",
    icon: <InboxOutlined className={styles.footerIcon} />,
    topBarExtraIcons: "FilterIcon",
  },
  {
    key: routes.Insights.path,
    title: "Insights",
    icon: <BarChartOutlined className={styles.footerIcon} />,
  },
  {
    key: routes.clinical.path,
    title: "Clinical",
    icon: <MedicineBoxOutlined className={styles.footerIcon} />,
  },
  {
    key: routes.watchlist.path,
    title: "Watchlist",
    icon: <RiseOutlined className={styles.footerIcon} />,
  },

  {
    key: routes.myFacility.path,
    title: "Consents & devices",
    icon: <SolutionOutlined className={styles.footerIcon} />,
  },
];

export const tabsWeb: ITabObject[] = [
  {
    key: routes.overview.path,
    title: "Dashboard",
    icon: <AppstoreOutlined className={styles.footerIcon} />,
  },
  ...deviceOrdersTabs,
  {
    key: routes.dashboard.path,
    title: "Inbox",
    icon: <InboxOutlined className={styles.footerIcon} />,
    topBarExtraIcons: "FilterIcon",
  },
  {
    key: routes.Insights.path,
    title: "Insights",
    icon: <BarChartOutlined className={styles.footerIcon} />,
  },
  {
    key: routes.clinical.path,
    title: "Clinical",
    icon: <MedicineBoxOutlined className={styles.footerIcon} />,
  },
  {
    key: routes.watchlist.path,
    title: "Watchlist",
    icon: <RiseOutlined className={styles.footerIcon} />,
  },
  {
    key: routes.myFacility.path,
    title: "Consents & devices",
    icon: <SolutionOutlined className={styles.footerIcon} />,
  },
  {
    key: routes.billingModule.path,
    title: "Documentation",
    icon: <SignatureOutlined className={styles.footerIcon} />,
    // BP entry point (tabsWeb is used for BP sidebar). NPs use tabsWebNP.
    groups: ["MedicalGroup-BillingPractitioner", "VRN", "AccountManager"],
  },
  // Note: Visits and Calendar tabs are only for home_health_external users
  // They are rendered separately in MobileLayout when isHomeHealthExternal is true
];

export const tabsWebNP: ITabObject[] = [
  {
    key: routes.evaluationAndManagement.path,
    title: "Navigator",
    icon: <CompassOutlined className={styles.footerIcon} />,
  },
  ...deviceOrdersTabs,
  {
    key: routes.overview.path,
    title: "Dashboard",
    icon: <AppstoreOutlined className={styles.footerIcon} />,
  },
  {
    key: routes.dashboard.path,
    title: "Inbox",
    icon: <InboxOutlined className={styles.footerIcon} />,
    topBarExtraIcons: "FilterIcon",
  },
  {
    key: routes.Insights.path,
    title: "Insights",
    icon: <BarChartOutlined className={styles.footerIcon} />,
  },
  {
    key: routes.clinical.path,
    title: "Clinical",
    icon: <MedicineBoxOutlined className={styles.footerIcon} />,
  },
  {
    key: routes.watchlist.path,
    title: "Watchlist",
    icon: <RiseOutlined className={styles.footerIcon} />,
  },

  {
    key: routes.myFacility.path,
    title: "Consents & devices",
    icon: <SolutionOutlined className={styles.footerIcon} />,
  },
  {
    key: routes.billingModule.path,
    title: "Documentation",
    icon: <SignatureOutlined className={styles.footerIcon} />,
    // NP (VRN) entry point — label/order unchanged for non-BP users.
    groups: ["MedicalGroup-BillingPractitioner", "VRN", "AccountManager"],
  },
  // Note: Visits and Calendar tabs are only for home_health_external users
  // They are rendered separately in MobileLayout when isHomeHealthExternal is true
];

export const premiumTabs: ITabObject[] = [
  {
    key: routes.motionAndExits.path,
    title: "Motion & exits",
    icon: <WarningOutlined className={styles.footerIcon} />,
  },
  {
    key: routes.vitalSync.path,
    title: "Vitals",
    icon: <SyncOutlined className={styles.footerIcon} />,
  },
  {
    key: routes.billing.path,
    title: "Billing Admin",
    icon: <DollarCircleFilled className={styles.footerIcon} />,
    groups: ["MedicalGroup-Administrator"], // User must belong to one of these groups to see the Billing tab
  },
  {
    key: routes.intercom.path,
    title: "Intercom",
    icon: <AudioOutlined className={styles.footerIcon} />,
  },
  {
    key: routes.iq.path,
    title: "IQ",
    icon: <OpenAIOutlined className={styles.footerIcon} />,
  },
  {
    key: routes.medRecAI.path,
    title: "Medications",
    icon: <ReconciliationOutlined className={styles.footerIcon} />,
  },
  // {
  //   key: routes.rtBilling.path,
  //   title: "RT",
  //   icon: <DollarOutlined className={styles.footerIcon} />,
  // },
  {
    // Superadmin-only internal dashboard. Gated to the Cognito `sa` group
    // here (sidebar visibility) and again by SuperadminRouteGuard + the
    // backend's get_current_admin dependency.
    key: routes.productUtilizationDashboard.path,
    title: "Product Utilization",
    icon: <BarChartOutlined className={styles.footerIcon} />,
    groups: ["sa"],
  },
  {
    // Superadmin-only internal dashboard. Gated to the Cognito `sa` group
    // here (sidebar visibility) and again by SuperadminRouteGuard + the
    // backend's get_current_admin dependency.
    key: routes.npProductivityTimeline.path,
    title: "NP Productivity Timeline",
    icon: <FieldTimeOutlined className={styles.footerIcon} />,
    groups: ["sa"],
  },
];

export const settingsTabs: ISupportTabObject[] = [
  {
    key: routes.settings.path,
    title: "Settings",
    icon: <SettingOutlined className={styles.footerIcon} />,
  },
  {
    key: "help-docs",
    title: "Help",
    icon: <QuestionCircleOutlined className={styles.footerIcon} />,
  },
];
